import Tooltip from "../../ui/ToolTip"
import { FaInfoCircle } from "react-icons/fa";
import { useEffect, useMemo, useState } from "react";

const InputRowValue = ({ 
  width = "w-[2rem]",
  className=`text-xs rounded-md px-2 py-1 ${width} border border-gray-300`,
  required, 
  title, 
  value, 
  setValue, 
  placeholder, 
  type, 
  options, 
  min, 
  max, 
  info, 
  disabled,
  minChars = 2, 
  rows = 1,
  onSelectOption, 
  suffix,
}) => {
  // Local state for autocomplete type
  const [query, setQuery] = useState("")
  const [openAutocomplete, setOpenAutocomplete] = useState(false)

  useEffect(() => {
    if (type !== "autocomplete") return
    const selectedOption = (options || []).find(o => o.value === value)
    if (selectedOption) {
      setQuery(selectedOption.label)
    } else if (!value) {
      setQuery("")
    }
  }, [type, value, options])

  const filteredOptions = useMemo(() => {
    if (type !== "autocomplete") return []
    const lower = (query || "").toLowerCase()
    return (options || [])
      .filter(o => o.label?.toLowerCase().includes(lower))
      .slice(0, 10)
  }, [type, options, query])

  return (
    <div>
      <label className="flex flex-row items-center text-xs block text-gray-700 mb-1">
        {title}
        {required && <span className="text-red-500">*</span>}
        {info && 
          <Tooltip content={info}>
            <div className="inline-block ml-1 hover:cursor-pointer">
              <FaInfoCircle className="inline-block ml-1" />
            </div>
          </Tooltip>
        }
      </label>
      <div className="flex">
        {type === "autocomplete" ? (
          <div className="relative w-full">
            <input
              type="text"
              placeholder={placeholder}
              value={query}
              required={required}
              disabled={disabled}
              onChange={(e) => { setQuery(e.target.value); setOpenAutocomplete(e.target.value.length >= minChars) }}
              onFocus={() => setOpenAutocomplete((query || "").length >= minChars)}
              onBlur={() => setTimeout(() => setOpenAutocomplete(false), 150)}
              className={`${className} ${suffix ? 'pr-16' : 'pr-8'}`}
            />
            {(query || value) && !disabled && (
              <button
                type="button"
                aria-label="Limpiar selección"
                className={`absolute ${suffix ? 'right-10' : 'right-2'} top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600`}
                onMouseDown={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  setQuery("")
                  setValue("")
                  if (onSelectOption) onSelectOption(null)
                  setOpenAutocomplete(false)
                }}
                title="Limpiar"
              >
                ×
              </button>
            )}
            {suffix && (
              <div className="absolute right-2 top-1/2 -translate-y-1/2 h-full flex items-center">
                {suffix}
              </div>
            )}
            {openAutocomplete && (query || "").length >= minChars && filteredOptions.length > 0 && (
              <ul className="absolute z-50 bg-white shadow-lg border rounded-md mt-1 max-h-56 overflow-auto w-full text-sm shadow">
                {filteredOptions.map((opt) => (
                  <li
                    key={opt.value}
                    className="px-3 text-xs text-gray-900 py-2 hover:bg-gray-100 cursor-pointer"
                    onMouseDown={() => {
                      setValue(opt.value)
                      setQuery(opt.label)
                      if (onSelectOption) onSelectOption(opt)
                      setOpenAutocomplete(false)
                    }}
                    title={opt.label}
                  >
                    {opt.label}
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : type === "select" ? (
          <div className="relative w-full">
            <select
              type={type}
              placeholder={placeholder}
              value={value}
              required={required}
              disabled={disabled}
              onChange={(e) => { 
                setValue(e.target.value); 
                if (onSelectOption) onSelectOption(e.target.value) 
              }}
              className={`${className} ${suffix ? 'pr-16' : ''}`}
            >
              {options.map((option, i) => (
                <option key={i} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            {suffix && (
              <div className="absolute right-2 top-1/2 -translate-y-1/2 h-full flex items-center">
                {suffix}
              </div>
            )}
          </div>
        ) : type === "textarea" ? (
          <div className="relative w-full">
            <textarea
              placeholder={placeholder}
              value={value}
              rows={rows}
              required={required}
              disabled={disabled}
              onChange={(e) => setValue(e.target.value)}
              className={`${className} ${suffix ? 'pr-16' : ''}`}
            />
            {suffix && (
              <div className="absolute right-2 top-2 h-full flex items-start">
                {suffix}
              </div>
            )}
          </div>
        ) : type === "date" ? (
          <div className="relative w-full">
            <input
              type="date"
              placeholder={placeholder}
              value={value}
              min={min}
              max={max}
              required={required}
              disabled={disabled}
              onChange={(e) => setValue(e.target.value)}
              className={`${className} ${suffix ? 'pr-16' : ''}`} 
            />
            {suffix && (
              <div className="absolute right-2 top-1/2 -translate-y-1/2 h-full flex items-center">
                {suffix}
              </div>
            )}
          </div>
        ) : type === "number" ? (
          <div className="relative w-full">
            <input
              type="number"
              placeholder={placeholder}
              value={value}
              min={min}
              max={max}
              required={required}
              disabled={disabled}
              onChange={(e) => setValue(e.target.value)}
              className={`${className} ${suffix ? 'pr-16' : ''}`} 
            />
            {suffix && (
              <div className="absolute right-2 top-1/2 -translate-y-1/2 h-full flex items-center">
                {suffix}
              </div>
            )}
          </div>
        ) : (
          <div className="relative w-full">
            <input
              type={type}
              placeholder={placeholder}
              value={value}
              min={min}
              max={max}
              required={required}
              disabled={disabled}
              onChange={(e) => setValue(e.target.value)}
              className={`${className} ${suffix ? 'pr-16' : ''}`} 
            />
            {suffix && (
              <div className="absolute right-2 top-1/2 -translate-y-1/2 h-full flex items-center">
                {suffix}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default InputRowValue;
